import React, {
  FC,
  FocusEvent,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';

import { GENERAL__COLOR_REGEX } from '../../../constants/forms';
import TextInput, { Props as TextInputProps } from '../TextInput';

import { validateHex } from './helpers';

type Props = TextInputProps;

/**
 * Just a wrapper around the native input component,
 * styled according to the design
 *
 * @param props             Props passed to the component
 * @param props.label       aria-label for the input
 * @param props.name        name attribute for the input
 * @param props.onChange    Callback to be invoked every time input changes
 * @param props.placeholder Input placeholder
 * @param props.required    Whether the input is required
 * @param props.value       The value of the input
 * @returns                 The component itself
 */
const HexColorInput: FC<Props> = ({
  label,
  name,
  onChange,
  placeholder,
  required,
  value,
}) => {
  const [color, setColor] = useState(value);
  const id = useId();

  // Take the color from props if the last typed color (in local state) is not valid
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (validateHex(event.target.value) === false) {
        setColor(value);
      }
    },
    [value],
  );

  // Trigger `onChange` handler only if the input value is a valid color
  const handleChange = useCallback(
    (newValue: string) => {
      setColor(newValue);

      if (validateHex(newValue)) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  // Update the local state when `color` property value is changed
  useEffect(() => {
    setColor(value);
  }, [value]);

  return (
    <TextInput
      id={id}
      label={label}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      pattern={GENERAL__COLOR_REGEX}
      placeholder={placeholder}
      required={required}
      slotProps={{
        htmlInput: {
          // Limit the input to 6 characters (plus "#")
          maxLength: 7,
        },
      }}
      spellCheck={false}
      value={color}
    />
  );
};

export default HexColorInput;
